import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import { registerDeviceWithWatchDuty } from 'shared/device';
import { promptUserToRateApp } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { VerifyPaymentData } from 'shared/types';
import { useEnableFlightTrackerLayer } from 'features/mapping/hooks/useEnableFlightTrackerLayer';
import { API } from 'api';
import { ANALYTICS_EVENTS, logEvent } from 'shared/analytics';
import { enqueueSnackbar } from 'notistack';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { logUnexpectedErrorAndNotifyUser } from 'shared/handleUnexpectedError';
import { useHistory } from 'react-router-dom';
import { useMembershipExternalSuccessQueryParams } from 'features/membership/hooks/useMembershipUrlParams';

const verifyPayment = async (data: {
  clientToken: string;
}): Promise<VerifyPaymentData> => {
  const response = await API.post<
    { clientToken: string },
    AxiosResponse<VerifyPaymentData>
  >('recurly_integration/payment_verification/', data);
  return response.data;
};

/**
 * We need to handle external payment success through URL params in cases where the membership/donation
 * flow happens outside of the native app in the device's browser, where we don't have access to
 * app's state.
 */
export const useHandleExternalPaymentSuccess = (): { isPending: boolean } => {
  const { t } = useTranslation();
  const { loginSuccess, hasActiveMembership } = useAuthState();
  const { enableFlightTrackerLayer } = useEnableFlightTrackerLayer();
  const history = useHistory();

  const {
    clientToken,
    donationSuccess,
    transaction,
    membershipSuccess,
    membershipUpgrade,
  } = useMembershipExternalSuccessQueryParams();

  const { isPending, mutate } = useMutation({
    mutationFn: verifyPayment,
    onSuccess: async (data) => {
      const isDonation = transaction === 'donation';
      const successMessage = isDonation
        ? t('donationSuccess.snackbarMessage')
        : t('membershipSuccess.snackbarMessage');
      loginSuccess(data.key, data.user);
      enqueueSnackbar(successMessage, { variant: 'success' });
      if (!isDonation) {
        enableFlightTrackerLayer();
      }
      logEvent({
        name: isDonation
          ? ANALYTICS_EVENTS.SUPPORT.SUCCESS_DONATION
          : ANALYTICS_EVENTS.SUPPORT.SUCCESS_MEMBERSHIP,
        params: {},
      });
      // associate user with token
      const params = { user: data.user };
      registerDeviceWithWatchDuty(params);
      await promptUserToRateApp();
    },
    onError: (error) => {
      logUnexpectedErrorAndNotifyUser(t, { error });
    },
  });

  useEffect(() => {
    if (clientToken) {
      // Anonymous user has become a member or made a donation.
      mutate({ clientToken });
      // remove query params
      history.replace({ search: '' });
    }
  }, [clientToken, history, mutate]);

  useEffect(() => {
    if (donationSuccess) {
      (async (): Promise<void> => {
        // A user has made a donation.
        await promptUserToRateApp();
        // remove query params
        history.replace({ search: '' });
      })();
    }
  }, [donationSuccess, history]);

  useEffect(() => {
    if (membershipSuccess) {
      (async (): Promise<void> => {
        enqueueSnackbar(t('membershipSuccess.snackbarMessage'), {
          variant: 'success',
        });
        enableFlightTrackerLayer();
        if (!hasActiveMembership) {
          // Existing user has become a member/Membership registration on android.
          // We need to invalidate the auth user query to ensure that
          // the user's membership status is updated and they can access members features.
          await persistedQueryClient.invalidateQueries({
            queryKey: ['auth-user'],
          });
        }
        await promptUserToRateApp();
        // remove query params
        history.replace({ search: '' });
      })();
    }
  }, [
    enableFlightTrackerLayer,
    hasActiveMembership,
    membershipSuccess,
    t,
    history,
  ]);

  useEffect(() => {
    if (membershipUpgrade) {
      (async (): Promise<void> => {
        enqueueSnackbar(t('membershipSuccess.upgradeMessage'), {
          variant: 'success',
        });
        // A user has upgraded their basic membership to a pro membership.
        // We need to invalidate the auth user query to ensure that
        // the user's membership status is updated and they can access pro features.
        await persistedQueryClient.invalidateQueries({
          queryKey: ['auth-user'],
        });
        await promptUserToRateApp();
        // remove query params
        history.replace({ search: '' });
      })();
    }
  }, [membershipUpgrade, history, t]);

  return { isPending };
};
